import shaderTxt from "./shader.glsl" with { type: "text" };
import { useEffect, useRef, useState } from "react";

const SHOW_FPS_COUNTER = false;

export function Shader(props: Readonly<{ className: string }>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0, z: 0, normalizedX: 0.0 }); // Added normalizedX
  const panOffsetRef = useRef(0.0); // For accumulating pan
  const lastAnimationTimeRef = useRef(performance.now()); // For deltaTime calculation
  const touchInProgressRef = useRef(false); // To track active touch gestures
  const [fps, setFps] = useState(0); // State for FPS display
  const lastFpsUpdateTimeRef = useRef(performance.now()); // For FPS calculation
  const frameCountRef = useRef(0); // For FPS calculation
  const MAX_PAN_SPEED = 5.0; // Max pan speed as requested
  const MAX_DELTA_TIME = 1.0 / 30.0; // Max deltaTime to prevent jumps
  const RENDER_DISTANCE = 100.0; // Increased to observe distant rendering

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl");
    if (!gl) {
      console.error("WebGL not supported");
      return;
    }

    const ext = gl.getExtension("OES_standard_derivatives");
    if (!ext) {
      console.warn("OES_standard_derivatives extension not supported");
    }

    const vertexShaderSource = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const compileShader = (type: number, source: string) => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error("Shader compile error:", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = compileShader(gl.FRAGMENT_SHADER, shaderTxt);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram(); // Added non-null assertion for consistency
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return;
    }
    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    const positions = [-1, -1, -1, 1, 1, -1, 1, 1];
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

    const positionAttribute = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(positionAttribute);
    gl.vertexAttribPointer(positionAttribute, 2, gl.FLOAT, false, 0, 0);

    const resolutionLoc = gl.getUniformLocation(program, "iResolution");
    const timeLoc = gl.getUniformLocation(program, "iTime");
    // const mouseLoc = gl.getUniformLocation(program, "iMouse"); // REMOVED
    const panOffsetLoc = gl.getUniformLocation(program, "u_panOffset"); // Get new uniform location
    const renderDistanceLoc = gl.getUniformLocation(
      program,
      "u_renderDistance",
    ); // Get new uniform location

    if (resolutionLoc === null)
      console.error("Uniform iResolution not found in shader");
    if (timeLoc === null) console.error("Uniform iTime not found in shader");
    // if (mouseLoc === null) console.error("Uniform iMouse not found in shader"); // REMOVED

    gl.uniform1i(gl.getUniformLocation(program, "uMaxSteps"), 100);
    gl.uniform1f(gl.getUniformLocation(program, "uTMax"), 100.0);
    gl.uniform1f(gl.getUniformLocation(program, "uHitTolerance"), 0.01);
    if (renderDistanceLoc) {
      gl.uniform1f(renderDistanceLoc, RENDER_DISTANCE);
    }

    let mouseDown = false;
    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const newMouseX = event.clientX - rect.left;
      const newMouseY = rect.height - (event.clientY - rect.top);

      // Update mouseRef if:
      // 1. No touch is in progress (normal mouse operation).
      // OR
      // 2. A touch IS in progress AND the mouse button is down (this handles emulated touch-drag,
      //    allowing mousemove to drive updates if emulated touchmove is sparse/absent).
      if (
        !touchInProgressRef.current ||
        (touchInProgressRef.current && mouseDown)
      ) {
        mouseRef.current.x = newMouseX;
        mouseRef.current.y = newMouseY;

        if (rect.width > 0) {
          mouseRef.current.normalizedX = (newMouseX / rect.width) * 2.0 - 1.0;
        }
        // If rect.width is not > 0, normalizedX retains its previous value.

        // If mouse button is down (covers normal mouse drag and emulated touch drag),
        // update z to indicate active drag, based on current x.
        if (mouseDown) {
          mouseRef.current.z = newMouseX;
        }
      }
      // If touchInProgressRef.current is true BUT mouseDown is false (e.g., touch just ended,
      // or mouse is just hovering during an active touch session without dragging),
      // then mousemove events do not update mouseRef. This allows touch handlers
      // like touchend or ongoing touchmove (if any) to be authoritative.
    };
    const handleMouseDown = () => {
      mouseDown = true;
    };
    const handleMouseUp = () => {
      if (touchInProgressRef.current) {
        // If a touch gesture is active or just ended, let touchend handle mouseDown state
        // This prevents emulated mouseup from clearing mouseDown prematurely during touch
        return;
      }
      mouseDown = false;
      mouseRef.current.z = 0;
    };
    window.addEventListener("mousemove", handleMouseMove); // Changed from canvas
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        event.preventDefault(); // Prevent default browser actions
        touchInProgressRef.current = true; // Mark touch as active
        mouseDown = true; // Indicate an active touch/drag operation
        const rect = canvas.getBoundingClientRect();
        mouseRef.current.x = touch.clientX - rect.left;
        mouseRef.current.y = rect.height - (touch.clientY - rect.top);
        if (rect.width > 0) {
          mouseRef.current.normalizedX =
            (mouseRef.current.x / rect.width) * 2.0 - 1.0; // Use mouseRef.current.x for calculation
        } else {
          mouseRef.current.normalizedX = 0.0; // Default on start if rect is invalid
        }
        mouseRef.current.z = mouseRef.current.x; // Set z based on x, like desktop
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!mouseDown) return; // Only process if touch started correctly
      const touch = event.touches[0];
      if (touch) {
        event.preventDefault(); // Prevent default browser actions during move
        const rect = canvas.getBoundingClientRect();
        mouseRef.current.x = touch.clientX - rect.left;
        mouseRef.current.y = rect.height - (touch.clientY - rect.top);
        if (rect.width > 0) {
          mouseRef.current.normalizedX =
            (mouseRef.current.x / rect.width) * 2.0 - 1.0; // Use mouseRef.current.x for calculation
        } else {
          mouseRef.current.normalizedX = 0.0; // Default to no pan if width is invalid
        }
        mouseRef.current.z = mouseRef.current.x; // Set z based on x, like desktop
      }
    };

    const handleTouchEnd = () => {
      mouseDown = false;
      mouseRef.current.z = 0;
      touchInProgressRef.current = false; // Mark touch as ended
    };

    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    // canvas.addEventListener("mouseup", handleMouseUp);

    // Load the terrain texture - REMOVED
    // const texture = gl.createTexture();
    // gl.bindTexture(gl.TEXTURE_2D, texture);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // const img = new Image();
    // img.onload = () => {
    //   gl.bindTexture(gl.TEXTURE_2D, texture);
    //   gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    //   console.log("Terrain texture loaded.");
    // };
    // img.onerror = (err) => {
    //   console.error("Failed to load terrain texture:", err);
    // };
    // img.src = terrainImage as string;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      const fullW = parent ? parent.clientWidth : window.innerWidth;
      const fullH = parent ? parent.clientHeight : window.innerHeight;
      const DPR = window.devicePixelRatio;
      const rw = Math.floor(fullW * DPR);
      const rh = Math.floor(fullH * DPR);

      if (canvas.width !== rw || canvas.height !== rh) {
        canvas.width = rw;
        canvas.height = rh;
      }

      gl.viewport(0, 0, rw, rh);
    };

    resizeCanvas();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });

      if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
      }
    }

    window.addEventListener("resize", resizeCanvas);

    const shaderStartTime = performance.now(); // Renamed to avoid conflict
    let animationFrameId: number;

    const animate = () => {
      // FPS Calculation
      const currentTime = performance.now();
      frameCountRef.current++;
      const timeSinceLastFpsUpdate = currentTime - lastFpsUpdateTimeRef.current;

      if (timeSinceLastFpsUpdate >= 1000) {
        // Update FPS display every second
        const calculatedFps =
          (frameCountRef.current * 1000) / timeSinceLastFpsUpdate;
        setFps(calculatedFps);
        frameCountRef.current = 0;
        lastFpsUpdateTimeRef.current = currentTime;
      }

      // Shader animation time
      const t = (performance.now() - shaderStartTime) / 1000;
      gl.useProgram(program);

      const currentAnimationTime = performance.now();
      const rawDeltaTime =
        (currentAnimationTime - lastAnimationTimeRef.current) / 1000.0;
      lastAnimationTimeRef.current = currentAnimationTime;
      const deltaTime = Math.min(rawDeltaTime, MAX_DELTA_TIME);

      // Pan is now always active based on mouse position
      const panVelocity = mouseRef.current.normalizedX * MAX_PAN_SPEED * -1.0; // Reversed direction
      panOffsetRef.current += panVelocity * deltaTime;

      if (resolutionLoc) {
        gl.uniform3f(resolutionLoc, canvas.width, canvas.height, 1.0);
      }
      if (timeLoc) gl.uniform1f(timeLoc, t);
      /* REMOVED iMouse uniform update block
      if (mouseLoc)
        gl.uniform4f(
          mouseLoc,
          mouseRef.current.x,
          mouseRef.current.y,
          mouseRef.current.z,
          0.0,
        );
      */
      if (panOffsetLoc) {
        gl.uniform1f(panOffsetLoc, panOffsetRef.current);
      }
      // It's good practice to set uniforms that don't change per frame during setup,
      // but if RENDER_DISTANCE could change, it would be set here too.

      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove); // Changed from canvas
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      gl.useProgram(null);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      gl.deleteBuffer(positionBuffer);
      // gl.deleteTexture(texture); // Clean up texture - REMOVED
    };
  }, [MAX_DELTA_TIME]);

  return (
    <>
      <canvas ref={canvasRef} className={props.className} />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {SHOW_FPS_COUNTER && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            color: "white",
            backgroundColor: "rgba(0,0,0,0.7)",
            padding: "2px 5px",
            borderRadius: "3px",
            fontFamily: "monospace",
            fontSize: "14px",
            zIndex: 1, // Ensure it's on top
          }}
        >
          FPS: {fps.toFixed(1)}
        </div>
      )}
    </>
  );
}
